/**
 * Phone Utilities
 * Ported from breitling-storefront-next/src/lib/phone.ts
 */

import type { CountryCode, PhoneNumber } from 'libphonenumber-js';
import {
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumber as parsePhone,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { isValidPhoneNumber as isValid } from 'libphonenumber-js/max';
import examples from 'libphonenumber-js/mobile/examples';

import { COUNTRY_CALLING_CODES } from '@/lib/phonePrefixes/const';

export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  try {
    return isValid(phoneNumber);
  } catch (_e) {
    return false;
  }
};

export const getPhoneCode = (phoneNumber: string) => {
  try {
    const number = getCountryCallingCode(phoneNumber as CountryCode);
    return number.toString();
  } catch (_e) {
    return { code: -1, msg: 'Failed to getCountryCallingCode' };
  }
};

export const parsePhoneNumber = (phone: string): PhoneNumber | null => {
  try {
    if (!isValidPhoneNumber(phone)) return null;
    return parsePhone(phone);
  } catch (_error) {
    return null;
  }
};

export const getPhoneCodeAndNumber = (phone?: string | null) => {
  const defaultPhonePrefix = '+1';
  if (phone && isValidPhoneNumber(phone)) {
    const phoneNumber = parsePhoneNumberWithError(phone);
    const phonePrefix =
      COUNTRY_CALLING_CODES.find((item) => item.code === phoneNumber.country)?.value ??
      defaultPhonePrefix;
    return {
      phoneCode: phonePrefix,
      phoneNumber: formatNationalByPhonePrefix(phonePrefix, phoneNumber.formatNational(), false),
    };
  }

  return {
    phoneCode: defaultPhonePrefix,
  };
};

export const getExamplePhone = (code: CountryCode | string): string => {
  const phoneNumber = getExampleNumber(code as CountryCode, examples);
  const formatNational = phoneNumber?.formatNational();
  const prefix = COUNTRY_CALLING_CODES.find((e) => e.code === code)?.value;
  if (!formatNational || !prefix) return '';
  const areaCodeByPrefix = prefix.split('-')[1];
  const pattern = new RegExp(`^\\(${areaCodeByPrefix}\\)\\s+`);
  if (areaCodeByPrefix && pattern.test(formatNational)) {
    return formatNational.replace(pattern, '');
  }
  return formatNational;
};

export const formatNationalByCountryCode = (
  code: CountryCode | string,
  national?: string
): string => {
  const prefix = COUNTRY_CALLING_CODES.find((e) => e.code === code)?.value;
  return formatNationalByPhonePrefix(prefix, national);
};

export const formatNationalByPhonePrefix = (
  prefix?: string,
  national?: string,
  isFormat = true
): string => {
  if (!national) return '';

  let phoneNumberWithoutPrefix = national;
  const formatPattern = /\D/g;

  if (prefix) {
    const areaCodeByPrefix = prefix.split('-')[1]?.trim();

    if (areaCodeByPrefix && /^\d+$/.test(areaCodeByPrefix)) {
      const pattern = new RegExp(`^\\(${areaCodeByPrefix}\\)\\s+`);

      phoneNumberWithoutPrefix = phoneNumberWithoutPrefix.replace(pattern, '');
    }
  }

  return isFormat ? phoneNumberWithoutPrefix : phoneNumberWithoutPrefix.replace(formatPattern, '');
};

export const formatPhoneWithCallingCode = (phone?: string, countryCode?: string): string => {
  const rawPhoneNumber = phone?.trim();
  if (!rawPhoneNumber || rawPhoneNumber === '') return '';
  if (!countryCode || countryCode.trim() === '') return phone || '';

  // If already starts with +, return as-is
  if (rawPhoneNumber.startsWith('+')) return rawPhoneNumber;

  const codeUpper = countryCode?.toUpperCase();
  const callingCode = COUNTRY_CALLING_CODES.find(
    (c: { code: string; value: string }) => c.code === codeUpper
  )?.value;

  if (!callingCode) {
    return `+${rawPhoneNumber}`;
  }

  const digitsOnly = (val: string) => val.replace(/\D+/g, '');
  // Paypal or other sources might return the phone number with spaces or parenthesis
  const rawPhoneNumberDigits = digitsOnly(rawPhoneNumber);
  const callingDigits = digitsOnly(callingCode);

  const hasCallingCode =
    rawPhoneNumberDigits.startsWith(callingDigits) ||
    (callingDigits.length > 1 && // Other than US and CA for instance
      callingDigits.startsWith('1') && // Special case for NANP sub-codes like +1-684
      rawPhoneNumberDigits.startsWith(callingDigits.slice(1)));

  if (hasCallingCode) {
    return `+${rawPhoneNumber}`;
  }

  return `${callingCode}${rawPhoneNumber}`;
};

/**
 * Formats an E.164 phone string for user-facing display.
 * Returns the calling code prefix followed by the national format with parentheses.
 * Falls back to the raw input if parsing fails.
 *
 * Example Input: "+12015550123"
 * Example Output: "+1 (201) 555-0123"
 */
export const formatPhoneForDisplay = (phone: string): string => {
  try {
    const parsed = parsePhoneNumberWithError(phone);
    const country = parsed.country ?? '';
    const prefix = COUNTRY_CALLING_CODES.find((c) => c.code === country)?.value ?? '';
    const national = formatNationalByCountryCode(country, parsed.formatNational());
    return `${prefix} ${national}`.trim();
  } catch {
    return phone;
  }
};
